import { apiRequest } from '@/shared/lib/api-client';
import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

export type ScheduledStatus = 'pending' | 'sending' | 'sent' | 'failed' | 'cancelled';
export type ScheduledType = 'text' | 'image' | 'audio' | 'video' | 'document';
export type FiltroDeStatus = 'pending' | 'sent' | 'failed' | 'cancelled' | 'all';

export interface ScheduledMessage {
  id: number;
  ticket_id: number;
  sent_ticket_id: number | null;
  contact: { id: number; name: string | null; phone: string | null };
  channel: { name: string | null; number: string | null };
  protocol_number: string | null;
  created_by: { id: number; name: string };
  status: ScheduledStatus;
  message_type: ScheduledType;
  body: string | null;
  media: { id: number; name: string | null; mime: string | null } | null;
  open_ticket: boolean;
  send_at: string;
  processed_at: string | null;
  cancelled_at: string | null;
  error_code: string | null;
  error_message: string | null;
  created_at: string;
}

export interface ScheduledPage {
  items: ScheduledMessage[];
  next_offset: number | null;
  counts: { pending: number; sent: number; failed: number; cancelled: number };
}

export interface NovoAgendamento {
  ticket_id: number;
  message_type: ScheduledType;
  body: string | null;
  media_id: number | null;
  send_at: string;
  open_ticket: boolean;
}

export type EdicaoDeAgendamento = Partial<Omit<NovoAgendamento, 'ticket_id'>>;

export function createScheduledMessage(input: NovoAgendamento): Promise<{ id: number }> {
  return apiRequest('/scheduled-messages', { method: 'POST', body: input });
}

export function updateScheduledMessage(id: number, input: EdicaoDeAgendamento): Promise<unknown> {
  return apiRequest(`/scheduled-messages/${id}`, { method: 'PATCH', body: input });
}

export function cancelScheduledMessage(id: number): Promise<unknown> {
  return apiRequest(`/scheduled-messages/${id}/cancel`, { method: 'POST', body: {} });
}

function listScheduledMessages(status: FiltroDeStatus, offset: number): Promise<ScheduledPage> {
  const qs = new URLSearchParams({ status, offset: String(offset), limit: '50' });
  return apiRequest<ScheduledPage>(`/scheduled-messages?${qs.toString()}`);
}

export const AGENDAMENTOS_KEY = ['scheduled-messages'] as const;

export function useScheduledMessages(status: FiltroDeStatus) {
  return useInfiniteQuery<
    ScheduledPage,
    Error,
    InfiniteData<ScheduledPage>,
    readonly unknown[],
    number
  >({
    queryKey: [...AGENDAMENTOS_KEY, status],
    queryFn: ({ pageParam }) => listScheduledMessages(status, pageParam),
    initialPageParam: 0,
    getNextPageParam: (last) => last.next_offset ?? undefined,
    // Pendentes mudam sozinhas (o despachante envia a cada 30 s).
    refetchInterval: status === 'pending' || status === 'all' ? 30_000 : false,
  });
}

/** Invalida todas as listas (qualquer aba) depois de criar/editar/cancelar. */
export function useInvalidarAgendamentos(): () => Promise<void> {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: AGENDAMENTOS_KEY });
}

export function useCancelScheduledMessage() {
  const invalidar = useInvalidarAgendamentos();
  return useMutation({
    mutationFn: (id: number) => cancelScheduledMessage(id),
    onSuccess: () => invalidar(),
  });
}
